import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSiteData } from '../../context/SiteDataContext';
import { createOrder } from '../../services/api';
import { Copy, Check, AlertTriangle } from 'lucide-react';

const BuyNow = () => {
  const { user } = useAuth();
  const { depositWallets: dwData, presale } = useSiteData();
  const wallets = dwData?.wallets || [];
  const price = presale?.price || 0.000055;

  const [form, setForm] = useState({ wallet: '', amount: '', senderWallet: '', txHash: '' });
  const [copiedIdx, setCopiedIdx] = useState(-1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const tokensReceived = form.amount && !isNaN(form.amount) && Number(form.amount) > 0
    ? Math.floor(Number(form.amount) / price) : 0;

  const handleCopy = (addr, idx) => {
    navigator.clipboard.writeText(addr);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(-1), 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.wallet || !form.amount || !form.senderWallet || !form.txHash) return;
    setSubmitting(true);
    setError('');
    try {
      await createOrder({
        wallet: form.wallet,
        amount: Number(form.amount),
        senderWallet: form.senderWallet,
        txHash: form.txHash,
      });
      setSubmitted(true);
      setForm({ wallet: '', amount: '', senderWallet: '', txHash: '' });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit order');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h1 className="text-2xl lg:text-3xl font-bold text-yellow-400 mb-4">Buy Now (Manual Transfer)</h1>
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 mb-6 text-blue-300 text-sm">
        To complete your purchase, please send the exact amount to one of the wallet addresses listed on the right. After sending, fill out the form below with your transaction details so we can verify and credit your account.
      </div>
      {submitted && <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-6 text-green-400 text-sm">Payment notification submitted successfully! We will verify and credit your account within 24 hours.</div>}
      {error && <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 text-red-400 text-sm">{error}</div>}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#0d1117] border border-[#1a2333] rounded-2xl p-6">
          <h2 className="text-white font-bold text-lg mb-5">Payment Notification Form</h2>
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="text-gray-400 text-sm mb-2 block">Select Target Wallet</label>
              <select value={form.wallet} onChange={e => setForm({...form, wallet: e.target.value})} className="w-full bg-[#1a2333] border border-[#2a3a4a] rounded-lg px-4 py-3 text-white text-sm focus:border-yellow-400/50 focus:outline-none appearance-none">
                <option value="">-- Select Wallet --</option>
                {wallets.map((w, i) => (<option key={i} value={w.name}>{w.name}</option>))}
              </select>
            </div>
            <div className="mb-4">
              <label className="text-gray-400 text-sm mb-2 block">Amount Sent</label>
              <input type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} placeholder="0.00" className="w-full bg-[#1a2333] border border-[#2a3a4a] rounded-lg px-4 py-3 text-white text-sm focus:border-yellow-400/50 focus:outline-none" />
              <p className="flex items-center gap-1 text-yellow-500 text-xs mt-2"><AlertTriangle size={12} /> Minimum investment amount is 10 USDT.</p>
            </div>
            <div className="mb-4">
              <label className="text-gray-400 text-sm mb-2 block">Total MRS (Approx.)</label>
              <input type="text" readOnly value={tokensReceived.toLocaleString()} className="w-full bg-[#1a2333] border border-[#2a3a4a] rounded-lg px-4 py-3 text-white text-sm cursor-default" />
            </div>
            <div className="mb-4">
              <label className="text-gray-400 text-sm mb-2 block">Your Wallet (Sender)</label>
              <input type="text" value={form.senderWallet} onChange={e => setForm({...form, senderWallet: e.target.value})} placeholder="0x..." className="w-full bg-[#1a2333] border border-[#2a3a4a] rounded-lg px-4 py-3 text-white text-sm focus:border-yellow-400/50 focus:outline-none" />
            </div>
            <div className="mb-6">
              <label className="text-gray-400 text-sm mb-2 block">Transaction Hash (TXID)</label>
              <input type="text" value={form.txHash} onChange={e => setForm({...form, txHash: e.target.value})} placeholder="Hash..." className="w-full bg-[#1a2333] border border-[#2a3a4a] rounded-lg px-4 py-3 text-white text-sm focus:border-yellow-400/50 focus:outline-none" />
            </div>
            <button type="submit" disabled={submitting} className="w-full py-3.5 rounded-xl font-bold text-black text-sm bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-300 hover:to-amber-400 transition-all disabled:opacity-50">
              {submitting ? 'Submitting...' : 'Submit Payment Notification'}
            </button>
          </form>
        </div>
        <div className="bg-[#0d1117] border border-[#1a2333] rounded-2xl p-6">
          <h2 className="text-white font-bold text-lg mb-5">Official Deposit Wallets</h2>
          <div className="flex flex-col gap-4">
            {wallets.map((wallet, i) => (
              <div key={i} className="bg-[#1a2333] border border-[#2a3a4a] rounded-xl p-4">
                <p className="text-yellow-400 font-bold text-sm mb-1">{wallet.name}</p>
                <div className="flex items-center gap-2">
                  <p className="text-gray-400 text-xs font-mono flex-1 truncate">{wallet.address}</p>
                  <button onClick={() => handleCopy(wallet.address, i)} className="text-gray-400 hover:text-yellow-400 transition-colors flex-shrink-0">
                    {copiedIdx === i ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyNow;
